import { translateText } from '../hooks/useTranslation';
import { localIdioms } from '../data/expressions';
import { db } from '../lib/firebase';
import { ref, get, set, update, child } from 'firebase/database';
import { isGrammaticalCrossReference } from '../utils/grammarTranslation';


// Cache to prevent duplicate API requests
const wordCache = new Map<string, { data: DictionaryResponse; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Pending requests to prevent duplicate simultaneous calls
const pendingRequests = new Map<string, Promise<DictionaryResponse>>();

// New API schema (freedictionaryapi.com)
export interface NewDictionaryResponse {
  word: string;
  entries: Array<{
    language: {
      code: string;
      name: string;
    };
    partOfSpeech: string;
    pronunciations: Array<{
      type: string;
      text: string;
      tags: string[];
    }>;
    forms: Array<{
      word: string;
      tags: string[];
    }>;
    senses: Array<{
      definition: string;
      tags: string[];
      examples: string[];
      quotes: Array<{
        text: string;
        reference: string;
      }>;
      synonyms: string[];
      antonyms: string[];
      translations?: Array<{
        language: {
          code: string;
          name: string;
        };
        word: string;
      }>;
      subsenses?: Array<{
        definition: string;
        tags: string[];
        examples: string[];
        quotes: Array<{
          text: string;
          reference: string;
        }>;
        synonyms: string[];
        antonyms: string[];
        translations?: Array<{
          language: {
            code: string;
            name: string;
          };
          word: string;
        }>;
        subsenses?: Array<any>; // circular reference
      }>;
    }>;
    synonyms: string[];
    antonyms: string[];
  }>;
  source: {
    url: string;
    license: {
      name: string;
      url: string;
    };
  };
}

// Old API schema (dictionaryapi.dev) - kept for fallback
export interface OldDictionaryResponse {
  word: string;
  phonetic: string;
  phonetics: Array<{
    text: string;
    audio: string;
  }>;
  meanings: Array<{
    partOfSpeech: string;
    definitions: Array<{
      definition: string;
      example?: string;
      synonyms: string[];
      antonyms: string[];
    }>;
  }>;
}

import { WordNuance, getWordNuance, getWordTranslationOverride } from '../data/wordNuances';

// Unified response interface
export interface DictionaryResponse {
  word: string;
  phonetic: string;
  phonetics: Array<{
    text: string;
    audio: string;
  }>;
  meanings: Array<{
    partOfSpeech: string;
    definitions: Array<{
      definition: string;
      example?: string;
      synonyms: string[];
      antonyms: string[];
    }>;
  }>;
  synonyms: string[];
  examples: string[];
  definition: string;
  audio: string;
  
  // Custom fields for expressions & nuances
  type?: string;
  literalMeaning?: string;
  exampleTranslation?: string;
  definitionTranslation?: string;
  translation?: string;
  exampleSentence?: string;
  nuance?: WordNuance | null;
}

// Helper function to remove duplicate phonetics (not used in new implementation)
// const uniqueText = (array: any[]) => array.filter(
//   (item, index, self) =>
//     self.findIndex((t) => t.text === item.text) === index
// );

// Helper function to prioritize substantive meanings over inflectional cross-reference stubs
export function prioritizeSubstantiveMeanings(
  meanings: Array<{
    partOfSpeech: string;
    definitions: Array<{
      definition: string;
      example?: string;
      synonyms: string[];
      antonyms: string[];
    }>;
  }>
): {
  meanings: typeof meanings;
  primaryDefinition: string;
} {
  if (!meanings || meanings.length === 0) {
    return { meanings: [], primaryDefinition: '' };
  }

  // A meaning is considered "pure grammatical cross-reference" if all its definitions are stubs
  const isPureGrammaticalMeaning = (m: (typeof meanings)[0]) =>
    m.definitions &&
    m.definitions.length > 0 &&
    m.definitions.every(d => isGrammaticalCrossReference(d?.definition));

  const hasSubstantiveMeaning = meanings.some(m => !isPureGrammaticalMeaning(m));

  let sortedMeanings = [...meanings];
  if (hasSubstantiveMeaning) {
    sortedMeanings.sort((a, b) => {
      const aGrammar = isPureGrammaticalMeaning(a);
      const bGrammar = isPureGrammaticalMeaning(b);
      if (aGrammar && !bGrammar) return 1;
      if (!aGrammar && bGrammar) return -1;
      return 0;
    });
  }

  // Find the first substantive definition across sorted meanings
  let primaryDef = '';
  for (const m of sortedMeanings) {
    for (const d of m.definitions || []) {
      if (d?.definition && !isGrammaticalCrossReference(d.definition)) {
        primaryDef = d.definition;
        break;
      }
    }
    if (primaryDef) break;
  }

  if (!primaryDef) {
    primaryDef = sortedMeanings[0]?.definitions?.[0]?.definition || '';
  }

  return {
    meanings: sortedMeanings,
    primaryDefinition: primaryDef
  };
}

// Helper function to parse new API schema (freedictionaryapi.com)
function parseNewApiResponse(data: NewDictionaryResponse): DictionaryResponse {
  const firstEntry = data.entries[0];
  const firstSense = firstEntry?.senses[0];
  
  // Extract phonetics from pronunciations
  const phonetics = firstEntry?.pronunciations?.map(pron => ({
    text: pron.text,
    audio: '' // New API doesn't provide audio URLs
  })) || [];
  
  // Extract meanings from entries
  const rawMeanings = data.entries.map(entry => ({
    partOfSpeech: entry.partOfSpeech,
    definitions: entry.senses.map(sense => ({
      definition: sense.definition,
      example: sense.examples?.[0] || '',
      synonyms: sense.synonyms || [],
      antonyms: sense.antonyms || []
    }))
  }));

  const prioritized = prioritizeSubstantiveMeanings(rawMeanings);
  
  // Extract all examples
  const examples = data.entries.flatMap(entry => 
    entry.senses.flatMap(sense => sense.examples || [])
  );
  
  // Extract all synonyms
  const synonyms = data.entries.flatMap(entry => [
    ...(entry.synonyms || []),
    ...entry.senses.flatMap(sense => sense.synonyms || [])
  ]);
  
  return {
    word: data.word,
    phonetic: phonetics[0]?.text || '',
    phonetics,
    meanings: prioritized.meanings,
    synonyms: [...new Set(synonyms)], // Remove duplicates
    examples: examples.filter(ex => ex && ex.trim()),
    definition: prioritized.primaryDefinition || firstSense?.definition || '',
    audio: '' // New API doesn't provide audio
  };
}

/**
 * Safe fetch with internal timeout to avoid hanging on 522 Cloudflare timeouts or unresponsive servers.
 */
async function fetchWithTimeout(url: string, options: RequestInit = {}, timeoutMs: number = 3000): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const parentSignal = options.signal;
  const onParentAbort = () => controller.abort();

  if (parentSignal) {
    if (parentSignal.aborted) {
      clearTimeout(timer);
      controller.abort();
    } else {
      parentSignal.addEventListener('abort', onParentAbort);
    }
  }

  try {
    const res = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    return res;
  } finally {
    clearTimeout(timer);
    if (parentSignal) {
      parentSignal.removeEventListener('abort', onParentAbort);
    }
  }
}

// Helper function to parse old API schema (dictionaryapi.dev)
function parseOldApiResponse(data: OldDictionaryResponse[]): DictionaryResponse {
  // Validate that data is an array with at least one entry
  if (!Array.isArray(data) || data.length === 0) {
    console.warn('parseOldApiResponse: Invalid data format, expected non-empty array');
    return {
      word: '',
      phonetic: '',
      phonetics: [],
      meanings: [],
      synonyms: [],
      examples: [],
      definition: '',
      audio: ''
    };
  }

  const firstEntry = data[0];
  
  // Validate firstEntry exists and has required properties
  if (!firstEntry || typeof firstEntry !== 'object') {
    console.warn('parseOldApiResponse: Invalid first entry');
    return {
      word: '',
      phonetic: '',
      phonetics: [],
      meanings: [],
      synonyms: [],
      examples: [],
      definition: '',
      audio: ''
    };
  }

  const mergedMeanings = data.flatMap(entry => entry?.meanings || []);
  const prioritized = prioritizeSubstantiveMeanings(mergedMeanings);

  return {
    word: firstEntry.word || '',
    phonetic: firstEntry.phonetic || '',
    phonetics: firstEntry.phonetics || [],
    meanings: prioritized.meanings,
    synonyms: [],
    examples: data.flatMap(entry => 
      entry?.meanings?.flatMap(meaning => 
        meaning?.definitions
          ?.filter(def => def?.example)
          .map(def => def.example!.replace(/—/g, "")) || []
      ) || []
    ),
    definition: prioritized.primaryDefinition || mergedMeanings[0]?.definitions?.[0]?.definition || '',
    audio: data.flatMap(entry => entry?.phonetics || []).find(p => p?.audio)?.audio || ''
  };
}

// Helper function to detect API schema
function detectApiSchema(data: unknown): 'new' | 'old' {
  // New API has 'entries' array with 'senses'
  if (data && typeof data === 'object' && 'entries' in data && Array.isArray((data as any).entries) && (data as any).entries[0]?.senses) {
    return 'new';
  }
  // Old API has 'meanings' array with 'definitions'
  if (data && typeof data === 'object' && 'meanings' in data && Array.isArray((data as any).meanings) && (data as any).meanings[0]?.definitions) {
    return 'old';
  }
  // Default to old for backward compatibility
  return 'old';
}

export function getPhraseCandidates(phrase: string): string[] {
  const words = phrase.toLowerCase().trim().split(/\s+/);
  if (words.length <= 1) return [];

  const candidates: string[] = [];
  const firstWord = words[0];
  const rest = words.slice(1).join(' ');

  // Present participle (-ing) -> base verb forms
  if (firstWord.endsWith('ing') && firstWord.length > 4) {
    // Double consonant: cutting -> cut, running -> run, stopping -> stop
    const doubleConsonantMatch = firstWord.match(/^(.+?)([bcdfghjklmnpqrstvwxyz])\2ing$/);
    if (doubleConsonantMatch) {
      candidates.push(`${doubleConsonantMatch[1]}${doubleConsonantMatch[2]} ${rest}`);
    }
    // Ends with e before ing: taking -> take, making -> make, giving -> give, biting -> bite
    const stem = firstWord.slice(0, -3);
    candidates.push(`${stem}e ${rest}`);
    // Simple -ing strip: calling -> call, breaking -> break, looking -> look
    candidates.push(`${stem} ${rest}`);
    // -ying -> -ie: dying -> die, lying -> lie, tying -> tie
    if (firstWord.endsWith('ying')) {
      candidates.push(`${firstWord.slice(0, -4)}ie ${rest}`);
    }
  }

  // Past tense / participle (-ed)
  if (firstWord.endsWith('ed') && firstWord.length > 3) {
    // Double consonant: stopped -> stop, dropped -> drop
    const doubleConsonantMatch = firstWord.match(/^(.+?)([bcdfghjklmnpqrstvwxyz])\2ed$/);
    if (doubleConsonantMatch) {
      candidates.push(`${doubleConsonantMatch[1]}${doubleConsonantMatch[2]} ${rest}`);
    }
    // liked -> like
    candidates.push(`${firstWord.slice(0, -1)} ${rest}`);
    // called -> call
    candidates.push(`${firstWord.slice(0, -2)} ${rest}`);
  }

  // 3rd person singular (-s / -es)
  if (firstWord.endsWith('es') && firstWord.length > 3) {
    candidates.push(`${firstWord.slice(0, -2)} ${rest}`);
  } else if (firstWord.endsWith('s') && firstWord.length > 2 && !firstWord.endsWith('ss')) {
    candidates.push(`${firstWord.slice(0, -1)} ${rest}`);
  }

  // Common irregular verbs
  const irregularMap: Record<string, string> = {
    'broke': 'break',
    'broken': 'break',
    'spoke': 'speak',
    'spoken': 'speak',
    'took': 'take',
    'taken': 'take',
    'went': 'go',
    'gone': 'go',
    'gave': 'give',
    'given': 'give',
    'got': 'get',
    'gotten': 'get',
    'fell': 'fall',
    'fallen': 'fall',
    'held': 'hold',
    'bought': 'buy',
    'brought': 'bring',
    'blew': 'blow',
    'blown': 'blow',
    'drew': 'draw',
    'drawn': 'draw',
    'caught': 'catch',
    'threw': 'throw',
    'thrown': 'throw',
    'chose': 'choose',
    'chosen': 'choose'
  };

  if (irregularMap[firstWord]) {
    candidates.push(`${irregularMap[firstWord]} ${rest}`);
  }

  return [...new Set(candidates.filter(c => c !== phrase))];
}

export function applyWordFixes(result: DictionaryResponse, normalizedWord: string): void {
  if (!result) return;

  // 1. General prioritization: promote substantive meanings over grammatical inflection stubs across all words
  if (result.meanings && result.meanings.length > 0) {
    const { meanings: prioritized, primaryDefinition } = prioritizeSubstantiveMeanings(result.meanings);
    result.meanings = prioritized;
    if (!result.definition || isGrammaticalCrossReference(result.definition)) {
      result.definition = primaryDefinition;
    }
  }

  // 2. Nuances and translation overrides
  if (!result.nuance) {
    const nuance = getWordNuance(normalizedWord);
    if (nuance) result.nuance = nuance;
  }
  const overrideTranslation = getWordTranslationOverride(normalizedWord);
  if (overrideTranslation) {
    result.translation = overrideTranslation;
  }

  // 3. Word-specific fixes
  if (normalizedWord === 'bearing' && result.meanings) {
    // Put noun first, adjective second, verb last
    result.meanings.sort((a, b) => {
      const posA = a.partOfSpeech?.toLowerCase();
      const posB = b.partOfSpeech?.toLowerCase();
      const order: Record<string, number> = { noun: 1, adjective: 2, verb: 3 };
      const valA = order[posA] || 9;
      const valB = order[posB] || 9;
      return valA - valB;
    });

    // Ensure primary definition and Portuguese definition translation are accurate
    result.definition =
      "Relevance, relationship or connection (e.g., 'have a bearing on'); a mechanical device that reduces friction (rolamento); or one's orientation or posture.";
    result.definitionTranslation =
      "Relação, relevância ou influência; rolamento mecânico; orientação (rumo); postura ou conduta.";

    // Sanitize any verb definition mentioning bear
    result.meanings.forEach(m => {
      if (m.partOfSpeech?.toLowerCase() === 'verb' && m.definitions) {
        m.definitions.forEach(d => {
          if (isGrammaticalCrossReference(d.definition)) {
            d.definition = "Present participle and gerund of the verb bear (to carry, endure, support, produce).";
          }
        });
      }
    });
  }

  if (normalizedWord === 'novel' && result.meanings) {
    const hasAdjective = result.meanings.some(m => m.partOfSpeech?.toLowerCase() === 'adjective');
    const hasNoun = result.meanings.some(m => m.partOfSpeech?.toLowerCase() === 'noun');
    if (!hasAdjective) {
      result.meanings.push({
        partOfSpeech: 'adjective',
        definitions: [
          {
            definition: 'New, original, and different from anything seen or known before (novo, inédito, original).',
            example: 'The scientists proposed a novel approach to solve the problem.',
            synonyms: ['new', 'original', 'innovative'],
            antonyms: ['old', 'traditional', 'familiar']
          }
        ]
      });
    }
    if (!hasNoun) {
      result.meanings.unshift({
        partOfSpeech: 'noun',
        definitions: [
          {
            definition: 'A fictitious prose narrative of book length, typically representing character and action with some degree of realism.',
            example: 'She just published her first historical novel.',
            synonyms: ['book', 'story', 'fiction'],
            antonyms: []
          }
        ]
      });
    }
  }
}

export async function searchWord(word: string, signal?: AbortSignal): Promise<DictionaryResponse> {
  const normalizedWord = word.toLowerCase().trim();
  const phraseCandidates = getPhraseCandidates(normalizedWord);
  
  // Check if it's a known idiom (exact or candidate base form)
  let idiom = localIdioms.find(i => i.phrase.toLowerCase() === normalizedWord);
  if (!idiom && phraseCandidates.length > 0) {
    idiom = localIdioms.find(i => phraseCandidates.includes(i.phrase.toLowerCase()));
  }
  if (idiom) {
    const result: DictionaryResponse = {
      word: idiom.phrase,
      phonetic: '',
      phonetics: [],
      meanings: [{
        partOfSpeech: 'idiom',
        definitions: [{
          definition: idiom.meaning,
          example: '',
          synonyms: [],
          antonyms: []
        }]
      }],
      synonyms: [],
      examples: [],
      definition: idiom.meaning,
      audio: '',
      type: 'idiom'
    };
    return result;
  }
  
  // Check RTDB cache
  try {
    const dbRef = ref(db);
    const snapshot = await get(child(dbRef, `vocabulary/${normalizedWord}`));
    if (snapshot.exists()) {
      const cachedData = snapshot.val() as DictionaryResponse;
      if (cachedData.meanings && cachedData.meanings.length > 0) {
        console.log(`Using RTDB cached data for word: ${normalizedWord}`);
        applyWordFixes(cachedData, normalizedWord);
        wordCache.set(normalizedWord, { data: cachedData, timestamp: Date.now() });
        return cachedData;
      }
    }
  } catch (error) {
    console.error('Failed to read from RTDB vocabulary cache', error);
  }

  // Check memory cache first
  const cached = wordCache.get(normalizedWord);
  if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
    console.log(`Using memory cached data for word: ${normalizedWord}`);
    return cached.data;
  }
  
  // Check if there's already a pending request for this word
  const pending = pendingRequests.get(normalizedWord);
  if (pending) {
    console.log(`Waiting for pending request for word: ${normalizedWord}`);
    return pending;
  }
  
  // Create the request promise and store it
  const requestPromise = (async () => {
    try {
      let result: DictionaryResponse = {
        word: '',
        phonetic: '',
        phonetics: [],
        definition: '',
        meanings: [],
        audio: '',
        examples: [],
        synonyms: []
      }

      let searchTerm = normalizedWord;
      const isMultiWord = searchTerm.split(" ").length > 1;

      let apiSuccess = false;

      // 1. Try Datamuse for multi-word phrases first
      if (isMultiWord) {
        try {
          const datamuseResponse = await fetch(`https://api.datamuse.com/words?sp=${encodeURIComponent(searchTerm)}&md=d&max=1`, { signal });
          if (datamuseResponse.ok) {
            const datamuseData = await datamuseResponse.json();
            if (datamuseData && datamuseData.length > 0 && datamuseData[0].word.toLowerCase() === searchTerm.toLowerCase() && datamuseData[0].defs) {
              const defs: string[] = datamuseData[0].defs;
              const meaningsMap = new Map<string, any>();
              
              defs.forEach(defStr => {
                const parts = defStr.split('\t');
                if (parts.length === 2) {
                  let pos = parts[0];
                  if (pos === 'v') pos = 'verb';
                  else if (pos === 'n') pos = 'noun';
                  else if (pos === 'adj') pos = 'adjective';
                  else if (pos === 'adv') pos = 'adverb';
                  else if (pos === 'u') pos = 'unknown';
                  
                  const definition = parts[1];
                  if (!meaningsMap.has(pos)) {
                    meaningsMap.set(pos, { partOfSpeech: pos, definitions: [] });
                  }
                  meaningsMap.get(pos).definitions.push({
                    definition, example: '', synonyms: [], antonyms: []
                  });
                }
              });
              
              if (meaningsMap.size > 0) {
                result.word = searchTerm;
                result.meanings = Array.from(meaningsMap.values());
                result.definition = result.meanings[0]?.definitions[0]?.definition || '';
                // Use definition as example fallback for Datamuse since md=d lacks examples
                result.examples = [result.definition]; 
                apiSuccess = true;
              }
            }
          }
        } catch (e) {
          console.warn('Datamuse phrase lookup failed:', e);
        }
      }

      let secondaryDataParsed: any = null;

      // 2. If Datamuse didn't work (or it's a single word), proceed with standard dictionary APIs
      if (!apiSuccess) {

        // 1. Primary: FreeDictionaryAPI (Wiktionary-backed OpenAPI)
        console.log(`Fetching word from API: ${searchTerm}`);
        try {
          const response = await fetchWithTimeout(
            `https://freedictionaryapi.com/api/v1/entries/en/${encodeURIComponent(searchTerm)}`,
            { signal },
            3000
          );
          
          if (response.ok) {
            const data = await response.json();
            if (data) {
              const schema = detectApiSchema(data);
              if (schema === 'new') {
                result = parseNewApiResponse(data as NewDictionaryResponse);
              } else {
                const dataArray = Array.isArray(data) ? data : [data];
                result = parseOldApiResponse(dataArray as OldDictionaryResponse[]);
                if (!result.word) {
                  result.word = searchTerm;
                }
              }
              if (result.meanings?.length > 0 && result.definition) {
                apiSuccess = true;
              }
            }
          } else {
            result.word = searchTerm;
          }
        } catch (e) {
          console.warn('FreeDictionaryAPI failed or timed out:', e);
          result.word = searchTerm;
        }

        // 2. Secondary: Wikimedia Wiktionary REST API (High availability, no API key required)
        if (!apiSuccess) {
          const searchTermsToTry = isMultiWord ? [searchTerm, ...phraseCandidates] : [searchTerm];
          for (const term of searchTermsToTry) {
            try {
              const wiktionaryRes = await fetchWithTimeout(
                `https://en.wiktionary.org/api/rest_v1/page/definition/${encodeURIComponent(term)}`,
                { signal },
                3000
              );
              if (wiktionaryRes.ok) {
                const wikiData = await wiktionaryRes.json();
                if (wikiData && wikiData.en && wikiData.en.length > 0) {
                  result.word = term;
                  result.meanings = wikiData.en.map((entry: any) => ({
                    partOfSpeech: (entry.partOfSpeech || (isMultiWord ? 'idiom' : 'noun')).toLowerCase(),
                    definitions: entry.definitions?.map((def: any) => {
                      const cleanDef = (def.definition || '').replace(/<[^>]*>/g, '').trim();
                      const rawExample = def.examples?.[0] || def.parsedExamples?.[0]?.example || '';
                      const cleanExample = (typeof rawExample === 'string' ? rawExample : '').replace(/<[^>]*>/g, '').trim();
                      return {
                        definition: cleanDef, 
                        example: cleanExample,
                        synonyms: [],
                        antonyms: []
                      };
                    }).filter((d: any) => d.definition.length > 0) || []
                  })).filter((m: any) => m.definitions.length > 0);
                  
                  for (const meaning of result.meanings) {
                    for (const def of meaning.definitions) {
                      if (def.definition && !def.definition.toLowerCase().includes('past participle')) {
                        if (!result.definition) result.definition = def.definition;
                        if (!result.examples.length && def.example) result.examples = [def.example];
                      }
                    }
                  }
                  if (!result.definition && result.meanings[0]?.definitions[0]?.definition) {
                    result.definition = result.meanings[0].definitions[0].definition;
                    if (!result.examples.length && result.meanings[0].definitions[0].example) {
                      result.examples = [result.meanings[0].definitions[0].example];
                    }
                  }
                  if (result.definition) {
                    if (isMultiWord) result.type = 'idiom';
                    apiSuccess = true;
                    break;
                  }
                }
              }
            } catch (e) {
              console.warn(`Wiktionary fallback failed for ${term}:`, e);
            }
          }
        }

        // 3. Tertiary: Datamuse API (Fast, ultra-reliable query engine, no auth)
        if (!apiSuccess && !isMultiWord) {
          try {
            const datamuseRes = await fetchWithTimeout(
              `https://api.datamuse.com/words?sp=${encodeURIComponent(searchTerm)}&md=dp&max=1`,
              { signal },
              2500
            );
            if (datamuseRes.ok) {
              const datamuseData = await datamuseRes.json();
              if (Array.isArray(datamuseData) && datamuseData.length > 0 && datamuseData[0].word.toLowerCase() === searchTerm.toLowerCase()) {
                const item = datamuseData[0];
                const defs: string[] = item.defs || [];
                const meaningsMap = new Map<string, any>();
                
                defs.forEach(defStr => {
                  const parts = defStr.split('\t');
                  if (parts.length === 2) {
                    let pos = parts[0];
                    if (pos === 'v') pos = 'verb';
                    else if (pos === 'n') pos = 'noun';
                    else if (pos === 'adj') pos = 'adjective';
                    else if (pos === 'adv') pos = 'adverb';
                    else if (pos === 'u') pos = 'unknown';
                    
                    const definition = parts[1];
                    if (!meaningsMap.has(pos)) {
                      meaningsMap.set(pos, { partOfSpeech: pos, definitions: [] });
                    }
                    meaningsMap.get(pos).definitions.push({
                      definition, example: '', synonyms: [], antonyms: []
                    });
                  }
                });

                const pronTag = item.tags?.find((t: string) => t.startsWith('pron:')) || item.tags?.find((t: string) => t.startsWith('ipa:'));
                if (pronTag) {
                  const pronText = '/' + pronTag.replace(/^(pron|ipa):/, '') + '/';
                  result.phonetic = pronText;
                  result.phonetics = [{ text: pronText, audio: '' }];
                }

                if (meaningsMap.size > 0) {
                  result.word = searchTerm;
                  result.meanings = Array.from(meaningsMap.values());
                  result.definition = result.meanings[0]?.definitions[0]?.definition || '';
                  result.examples = [result.definition];
                  apiSuccess = true;
                }
              }
            }
          } catch (e) {
            console.warn('Datamuse fallback failed:', e);
          }
        }

        // 4. Quaternary: Merriam-Webster Collegiate / Learner's Dictionary
        if (!apiSuccess && !isMultiWord) {
          const isDev = import.meta && (import.meta as any).env && (import.meta as any).env.DEV;
          const mwBase = isDev ? '/mw' : 'https://www.dictionaryapi.com';
          try {
            const response3 = await fetchWithTimeout(
              `${mwBase}/api/v3/references/learners/json/${encodeURIComponent(
                searchTerm
              )}?key=f8c54dc3-70d5-443d-89ae-9a70f4a112b1`,
              { signal },
              2500
            );
            if (response3.ok) {
              const secondaryDataParsed = await response3.json();
              if (secondaryDataParsed && Array.isArray(secondaryDataParsed) && secondaryDataParsed.length > 0 && typeof secondaryDataParsed[0] === 'object') {
                result.word = searchTerm;
                result.meanings = secondaryDataParsed.slice(0, 1).map((entry: any) => ({
                  partOfSpeech: entry.fl || 'noun',
                  definitions: [{
                    definition: entry.shortdef?.[0] || '',
                    example: secondaryDataParsed[0]?.def?.flatMap((defEntry: any) =>
                      defEntry.sseq?.flatMap((sseqEntry: any) =>
                        sseqEntry?.flatMap((senseItem: any) => {
                          const data = senseItem[1];
                          if (data && data.dt) {
                            return data.dt
                              .filter((dtEntry: any) => dtEntry[0] === 'vis')
                              .flatMap((dtEntry: any) =>
                                dtEntry[1].map((visEntry: any) => visEntry.t.replace(/—/g, ''))
                              );
                          }
                          return [];
                        }) || []
                      ) || []
                    ).join('\n\n') || '',
                    synonyms: [],
                    antonyms: []
                  }]
                }));
                result.definition = secondaryDataParsed[0]?.shortdef?.[0] || '';
                if (!result.examples.length && secondaryDataParsed[0]?.def) {
                  result.examples = secondaryDataParsed[0].def.flatMap((defEntry: any) =>
                    defEntry.sseq?.flatMap((sseqEntry: any) =>
                      sseqEntry?.flatMap((senseItem: any) => {
                        const data = senseItem[1];
                        if (data && data.dt) {
                          return data.dt
                            .filter((dtEntry: any) => dtEntry[0] === 'vis')
                            .flatMap((dtEntry: any) =>
                              dtEntry[1].map((visEntry: any) => visEntry.t.replace(/—/g, ''))
                            );
                        }
                        return [];
                      }) || []
                    ) || []
                  );
                }
                if (secondaryDataParsed[0]?.hwi?.prs) {
                  result.phonetics = secondaryDataParsed[0].hwi.prs.map((pr: any) => ({
                    text: pr.ipa,
                    audio: ''
                  }));
                  result.phonetic = result.phonetics[0]?.text || '';
                }
                if (result.definition) {
                  apiSuccess = true;
                }
              }
            }
          } catch (e) {
            console.warn('Merriam-Webster fallback failed:', e);
          }
        }

        // 5. Quinary: Try dictionaryapi.dev with timeout (Handles 522/downtime gracefully)
        // If not found yet OR if we want to enrich audio/phonetic details
        if (!isMultiWord && (!apiSuccess || !result.audio || !result.phonetic)) {
          try {
            const response2 = await fetchWithTimeout(
              `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(searchTerm)}`,
              { signal },
              2000
            );
            
            if (response2.ok) {
              const oldApiData = await response2.json();
              if (oldApiData && (Array.isArray(oldApiData) || typeof oldApiData === 'object')) {
                const dataArray = Array.isArray(oldApiData) ? oldApiData : [oldApiData];
                const oldResult = parseOldApiResponse(dataArray as OldDictionaryResponse[]);
                
                if (!result.meanings.length && oldResult.meanings.length) {
                  result.meanings = oldResult.meanings;
                }
                if (!result.definition && oldResult.definition) {
                  result.definition = oldResult.definition;
                }
                if (!result.examples.length && oldResult.examples.length) {
                  result.examples = oldResult.examples;
                }
                if (!result.phonetics.length && oldResult.phonetics.length) {
                  result.phonetics = oldResult.phonetics;
                  result.phonetic = oldResult.phonetic;
                }
                if (!result.audio && oldResult.audio) {
                  result.audio = oldResult.audio;
                }
                if (result.definition) {
                  apiSuccess = true;
                }
              }
            }
          } catch (e) {
            console.warn('dictionaryapi.dev fallback failed or timed out:', e);
          }
        }

        if (!apiSuccess) {
          throw new Error('Word not found');
        }
      }

      // Check if we actually have any valid definitions
      const hasValidDefinition = result.meanings?.some(meaning => 
        meaning.definitions?.some(def => {
          const defText = typeof def === 'string' ? def : def.definition;
          return defText && defText.trim().length > 0;
        })
      );

      if (!hasValidDefinition) {
        throw new Error('Word not found');
      }

      // Collect synonyms from the result (already processed by parseNewApiResponse or parseOldApiResponse)
      const allSynonyms = new Set(result.synonyms || []);

      console.log('All synonyms:', result.synonyms);

      // If we have synonyms from the API, use them
      if (result.synonyms && result.synonyms.length > 0) {
        result.synonyms = Array.from(allSynonyms);
      }

      let synonymSearchTerm = searchTerm;
      if(synonymSearchTerm?.split(" ").length > 1) {
        synonymSearchTerm = synonymSearchTerm.split(" ")[0];
      }

      // Try to get synonyms from multiple sources if not found
      if (result.synonyms.length === 0) {
        try {
          // Try Big Huge Thesaurus API first
          const synonymsResponse = await fetch(`https://words.bighugelabs.com/api/2/daa1b7d3ce377a25f733091f6ed7642d/${encodeURIComponent(synonymSearchTerm)}/json`, { signal });
          if (synonymsResponse.ok) {
            const synonymsData = await synonymsResponse.json();
            const bigHugeSynonyms = synonymsData?.noun?.syn || synonymsData?.verb?.syn || synonymsData?.adjective?.syn || synonymsData?.adverb?.syn || [];
            if (bigHugeSynonyms.length > 0) {
              result.synonyms = bigHugeSynonyms;
            }
          }
        } catch (error) {
          console.log('Big Huge Thesaurus API failed, trying alternatives...');
        }

        // If still no synonyms, try Datamuse API
        if (result.synonyms.length === 0) {
          try {
            const datamuseResponse = await fetch(`https://api.datamuse.com/words?rel_syn=${encodeURIComponent(synonymSearchTerm)}&max=10`, { signal });
            if (datamuseResponse.ok) {
              const datamuseData = await datamuseResponse.json();
              if (Array.isArray(datamuseData) && datamuseData.length > 0) {
                result.synonyms = datamuseData.map((item: any) => item.word).filter((w: string) => w.toLowerCase() !== synonymSearchTerm.toLowerCase());
              }
            }
          } catch (error) {
            console.log('Datamuse API failed, trying alternatives...');
          }
        }

        // If still no synonyms, try using related words from secondary data
        if (result.synonyms.length === 0 && secondaryDataParsed) {
          try {
            const secondaryData = secondaryDataParsed;
            
            if (secondaryData) {
              let relatedWords: string[] = [];
              
              // Check if secondaryData is an array of strings (direct word list)
              if (Array.isArray(secondaryData) && secondaryData.length > 0) {
                if (typeof secondaryData[0] === 'string') {
                  // It's an array of word strings
                  relatedWords = secondaryData
                    .filter((w: string) => {
                      const wordLower = w.toLowerCase();
                      const searchLower = synonymSearchTerm.toLowerCase();
                      // Filter out exact matches and words that are too different
                      return wordLower !== searchLower && 
                             wordLower.length >= searchLower.length - 3 && 
                             wordLower.length <= searchLower.length + 3;
                    })
                    .slice(0, 5);
                } else {
                  // It's an array of objects, try to extract stems or related words
                  relatedWords = secondaryData
                    .map((entry: any) => {
                      // Try multiple possible fields
                      if (entry.meta?.stems) return entry.meta.stems;
                      if (entry.meta?.syns) return entry.meta.syns;
                      if (entry.hwi?.hw) return [entry.hwi.hw];
                      return [];
                    })
                    .flat()
                    .filter((w: string) => {
                      const wordLower = w.toLowerCase();
                      const searchLower = synonymSearchTerm.toLowerCase();
                      return wordLower !== searchLower && 
                             wordLower.length >= searchLower.length - 3 && 
                             wordLower.length <= searchLower.length + 3;
                    })
                    .slice(0, 5);
                }
              }
              
              if (relatedWords.length > 0) {
                result.synonyms = relatedWords;
              }
            }
          } catch (error) {
            console.log('Failed to extract related words from secondary data:', error);
          }
        }

        // Last resort: try translation round-trip to find alternative words
        if (result.synonyms.length === 0) {
          try {
            const portugueseWord = await translateText(synonymSearchTerm, 'en', 'pt');
            const englishWord = await translateText(portugueseWord, 'pt', 'en');
            if (englishWord.toLowerCase() !== synonymSearchTerm.toLowerCase()) {
              // Try to find synonyms for the translated word
              const translatedResponse = await fetch(`https://api.datamuse.com/words?rel_syn=${encodeURIComponent(englishWord)}&max=5`, { signal });
              if (translatedResponse.ok) {
                const translatedData = await translatedResponse.json();
                if (Array.isArray(translatedData) && translatedData.length > 0) {
                  result.synonyms = translatedData.map((item: any) => item.word).filter((w: string) => w.toLowerCase() !== synonymSearchTerm.toLowerCase());
                }
              }
            }
          } catch (error) {
            console.log('Translation fallback failed');
          }
        }
      }

      // Sanitize formatting tags like {it}, {/it}, {b}, {bc} and HTML tags from all text fields
      const sanitize = (text: string) => text ? text.replace(/{[^}]*}/g, '').replace(/<[^>]*>/g, '') : text;

      if (result.definition) {
        result.definition = sanitize(result.definition);
      }
      if (result.examples && result.examples.length > 0) {
        result.examples = result.examples.map(ex => sanitize(ex));
      }
      if (result.meanings && result.meanings.length > 0) {
        result.meanings.forEach(meaning => {
          if (meaning.definitions) {
            meaning.definitions.forEach(def => {
              if (def.definition) {
                def.definition = sanitize(def.definition);
              }
              if (def.example) {
                def.example = sanitize(def.example);
              }
            });
          }
        });
      }

      console.log('result', result);
      
      applyWordFixes(result, normalizedWord);

      // Cache the result in memory
      wordCache.set(normalizedWord, { data: result, timestamp: Date.now() });
      
      // Cache the result in RTDB (only if we have valid meanings, to avoid caching "Not Found")
      if (result.meanings && result.meanings.length > 0) {
        try {
          const dbRef = ref(db, `vocabulary/${normalizedWord}`);
          // Use update instead of set to avoid wiping out the 'translation' field if it already exists
          await update(dbRef, result as any);
        } catch (error) {
          console.error('Failed to write to RTDB vocabulary cache', error);
        }
      }
      
      return result;
    } finally {
      // Remove from pending requests
      pendingRequests.delete(normalizedWord);
    }
  })();
  
  // Store the pending request
  pendingRequests.set(normalizedWord, requestPromise);
  
  return requestPromise;
}

export async function getSuggestions(word: string): Promise<string[]> {
  // Using Datamuse API for word suggestions based on prefix matching
  const response = await fetch(
    `https://api.datamuse.com/sug?s=${encodeURIComponent(word)}&max=5`
  );
  
  if (!response.ok) {
    return [];
  }
  
  const data = await response.json();
  return data.map((item: { word: string }) => item.word);
}